import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Send, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import PageBanner from './PageBanner';

export default function JoinPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    affiliation: '',
    membership_type: '',
    source: '',
    who: '',
    comments: '',
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleMembershipSelect = (type) => {
    setFormData((prev) => ({ ...prev, membership_type: type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.phone || !formData.affiliation) {
      toast.error('Please fill in all required fields.', {
        style: { borderRadius: '0', background: '#002233', color: '#fff' },
      });
      return;
    }

    if (!formData.membership_type) {
      toast.error('Please select whether you are a New Member or Renewing.', {
        style: { borderRadius: '0', background: '#002233', color: '#fff' },
      });
      return;
    }

    if (!formData.who) {
      toast.error('Please indicate which officer referred you (or write "None").', {
        style: { borderRadius: '0', background: '#002233', color: '#fff' },
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('registrations').insert([
        {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          affiliation: formData.affiliation,
          membership_type: formData.membership_type,
          source: formData.source,
          who: formData.who,
          comments: formData.comments,
        },
      ]);

      if (error) throw error;

      toast.success('Registration successful!', {
        icon: '🚀',
        style: { borderRadius: '0', background: '#006699', color: '#fff', fontWeight: 'bold' },
      });

      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Registration failed. Please try again.', {
        style: { borderRadius: '0', background: '#dc2626', color: '#fff' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafcff] relative overflow-hidden">
      {/* Background Architectural Grid Pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg, rgba(15, 105, 157, 0.02) 0px, rgba(15, 105, 157, 0.02) 1px, transparent 1px, transparent 18px)`,
        }}
      />

      <PageBanner
        title="Join IEEE ISIMS SB"
        description="Become a part of the world's largest technical professional organization for the advancement of technology."
        Icon={UserPlus}
      />

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white border border-[#006699]/25 shadow-2xl relative rounded-none"
        >
          {/* IEEE Tech Sharp Corner Brackets */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-[#006699] pointer-events-none z-20" />
          <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-[#006699] pointer-events-none z-20" />
          <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-[#006699] pointer-events-none z-20" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-[#006699] pointer-events-none z-20" />

          {/* Picture Banner with Clean Typography */}
          <div className="w-full h-52 sm:h-64 md:h-72 relative overflow-hidden flex items-center justify-center">
            <img
              src="/asset/event/anniv6.webp"
              alt="IEEE Anniversary"
              className="absolute inset-0 w-full h-full object-cover blur-[1.5px] scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#001824]/90 via-[#002b40]/65 to-[#001824]/50" />
            
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.8 }}
              className="relative z-10 text-center px-6"
            >
              <span className="text-[#7dd3fc] text-xs sm:text-sm font-mono uppercase tracking-[0.3em] font-semibold block mb-2">
                Official Registration
              </span>
              <p className="text-white text-2xl sm:text-3xl md:text-5xl font-light tracking-[0.16em] uppercase">
                <span className="text-[#7dd3fc] font-bold">#</span> Great_Stories_Begin_Here
              </p>
              <div className="h-[2px] w-20 bg-[#7dd3fc] mx-auto mt-4" />
              <p className="text-slate-300 text-xs sm:text-sm font-mono tracking-widest uppercase mt-3">
                IEEE ISIMS Student Branch
              </p>
            </motion.div>
          </div>

          {/* Form Content */}
          <div className="p-6 sm:p-12 lg:p-16 pt-10">
            <form onSubmit={handleSubmit} className="space-y-10">

              {/* ---------------------------------------------
                  STEP 01: MEMBERSHIP TYPE (Horizontal Big Selector)
                 --------------------------------------------- */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <label className="text-xs font-black text-[#00334d] uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="text-[#006699]">[ 01 ]</span> Membership Type <span className="text-red-500">*</span>
                  </label>
                  <span className="text-xs font-mono text-slate-400 uppercase tracking-widest">Select One</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {[
                    {
                      id: 'New Member',
                      label: 'New Member',
                      desc: 'First time joining IEEE ISIMS SB',
                    },
                    {
                      id: 'Renew',
                      label: 'Renew Membership',
                      desc: 'Continuing member for 2024–2025',
                    },
                  ].map((item) => {
                    const isSelected = formData.membership_type === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => handleMembershipSelect(item.id)}
                        className={`p-5 sm:p-6 border-2 cursor-pointer transition-all duration-200 rounded-none select-none flex items-center justify-between ${
                          isSelected
                            ? 'border-[#006699] bg-[#006699]/5 text-[#006699]'
                            : 'border-transparent bg-[#f4f6f9] hover:border-[#006699]/40 text-slate-700'
                        }`}
                      >
                        <div className="pr-4">
                          <span className={`block text-base sm:text-lg font-black uppercase tracking-wider ${isSelected ? 'text-[#006699]' : 'text-[#00334d]'}`}>
                            {item.label}
                          </span>
                          <span className="block text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">
                            {item.desc}
                          </span>
                        </div>
                        <div
                          className={`w-6 h-6 rounded-none border-2 flex items-center justify-center shrink-0 transition-colors ${
                            isSelected ? 'border-[#006699] bg-[#006699]' : 'border-slate-300 bg-white'
                          }`}
                        >
                          {isSelected && <div className="w-2.5 h-2.5 bg-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Architectural Thin Divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-[#006699]/25 to-transparent" />

              {/* ---------------------------------------------
                  STEP 02: APPLICANT IDENTITY (Horizontal 2-Column Grid)
                 --------------------------------------------- */}
              <div className="space-y-6">
                <div className="border-b border-slate-100 pb-3">
                  <label className="text-xs font-black text-[#00334d] uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="text-[#006699]">[ 02 ]</span> Applicant Information <span className="text-red-500">*</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider flex items-center gap-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="e.g. John Doe"
                      className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider flex items-center gap-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider flex items-center gap-1.5">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+216 55 555 555"
                      className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none"
                    />
                  </div>

                  {/* Study Affiliation */}
                  <div className="space-y-2">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider flex items-center gap-1.5">
                      Study Affiliation <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="affiliation"
                        required
                        value={formData.affiliation}
                        onChange={handleChange}
                        className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none appearance-none cursor-pointer"
                      >
                        <option value="" disabled>Select your affiliation</option>
                        <option value="Preparatory student">Preparatory student</option>
                        <option value="Engineering">Engineering</option>
                        <option value="Bachelor-License">Bachelor-License</option>
                        <option value="Masters">Masters</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-5 pointer-events-none text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Architectural Thin Divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-[#006699]/25 to-transparent" />

              {/* ---------------------------------------------
                  STEP 03: REFERRAL & QUESTIONS (Horizontal Layout)
                 --------------------------------------------- */}
              <div className="space-y-6">
                <div className="border-b border-slate-100 pb-3">
                  <label className="text-xs font-black text-[#00334d] uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="text-[#006699]">[ 03 ]</span> Referral & Discovery
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Which officer told you about us */}
                  <div className="space-y-2">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider flex items-center gap-1.5">
                      Which officer told you about us? <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="who"
                      required
                      value={formData.who}
                      onChange={handleChange}
                      placeholder="Officer name (or write None)"
                      className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none"
                    />
                  </div>

                  {/* How did you hear about us */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider">
                        How did you hear about us?
                      </label>
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 uppercase tracking-wider">
                        Optional
                      </span>
                    </div>
                    <input
                      type="text"
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      placeholder="e.g. Social Media, Friend, Event..."
                      className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none"
                    />
                  </div>
                </div>

                {/* Comments */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-extrabold text-[#00334d] uppercase tracking-wider">
                      Any question or comment?
                    </label>
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 uppercase tracking-wider">
                      Optional
                    </span>
                  </div>
                  <textarea
                    name="comments"
                    rows="4"
                    value={formData.comments}
                    onChange={handleChange}
                    placeholder="Write your message here..."
                    className="w-full bg-[#f4f6f9] border border-slate-200/80 focus:border-[#006699] focus:bg-white rounded-none px-5 py-4 text-base font-medium text-[#002233] transition-all duration-200 outline-none resize-none"
                  />
                </div>
              </div>

              {/* ---------------------------------------------
                  SUBMIT BUTTON & ETHICS CLAUSE
                 --------------------------------------------- */}
              <div className="pt-6 space-y-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#006699] hover:bg-[#00334d] text-white font-black text-sm sm:text-base uppercase tracking-[0.25em] py-5 sm:py-6 rounded-none transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer group disabled:opacity-75 disabled:cursor-not-allowed border-2 border-[#006699] hover:border-[#00334d]"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Processing Application...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Application</span>
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                <div className="flex items-start gap-3 pt-2 text-xs text-slate-500">
                  <AlertCircle className="w-4 h-4 text-[#006699] shrink-0 mt-0.5" />
                  <p>
                    By submitting this form, you agree to follow the IEEE Code of Ethics and actively participate in branch activities.
                  </p>
                </div>
              </div>

            </form>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
